import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { fmtDate, fmtINR } from "@/lib/leadUtils";

export default function EditorDetail() {
  const { editorId } = useParams();
  const navigate = useNavigate();
  const [editor, setEditor] = useState(null);
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [draft, setDraft] = useState({});

  const load = async () => {
    const [e, p] = await Promise.all([
      api.get(`/editors/${editorId}`),
      api.get(`/editors/${editorId}/projects`),
    ]);
    setEditor(e.data);
    setDraft({
      full_name: e.data.full_name, phone: e.data.phone_display || e.data.phone || "",
      email: e.data.email || "", specialty: e.data.specialty, city: e.data.city || "",
      active: e.data.active,
    });
    setProjects(p.data.items || []);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [editorId]);

  const save = async () => {
    try {
      await api.patch(`/editors/${editorId}`, {
        full_name: draft.full_name, phone: draft.phone || null, email: draft.email || null,
        specialty: draft.specialty, city: draft.city || null, active: draft.active,
      });
      toast.success("Saved");
      setEditing(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const resetPwd = async () => {
    if (!pwd || pwd.length < 8) { toast.error("Min 8 chars"); return; }
    try {
      await api.post(`/editors/${editorId}/reset-password`, { password: pwd });
      toast.success("Password reset");
      setResetOpen(false);
      setPwd("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!editor) return <Layout><div className="kicker" data-testid="editor-detail-loading">Loading</div></Layout>;
  const s = editor.stats || {};

  return (
    <Layout>
      <div className="fade-up" data-testid="editor-detail-page">
        <button onClick={() => navigate("/catalog")} className="text-xs tracking-[0.18em] uppercase text-brand-muted flex items-center gap-1 mb-4 hover:text-brand-ink">
          <ChevronLeft size={14} /> Back to Catalog
        </button>

        <div className="kicker mb-2">EDITOR</div>
        <h1 className="font-display mb-1" data-testid="editor-detail-name">{editor.full_name}</h1>
        <div className="text-sm text-brand-muted mb-8">
          {editor.specialty} · {editor.city || "—"} {!editor.active && <span className="ml-2 text-[10px] tracking-[0.18em] uppercase">DEACTIVATED</span>}
        </div>

        {/* Profile */}
        <div className="bg-white border border-brand-border p-6 mb-8" data-testid="editor-profile">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg" style={{ color: "var(--brand-ink)" }}>Profile</h2>
            {!editing ? (
              <div className="flex gap-2">
                <button onClick={() => setEditing(true)} className="text-xs tracking-[0.18em] uppercase text-brand-muted hover:text-brand-ink" data-testid="editor-detail-edit">Edit</button>
                <button onClick={() => setResetOpen(true)} className="text-xs tracking-[0.18em] uppercase text-brand-muted hover:text-brand-ink" data-testid="editor-detail-reset">Reset password</button>
              </div>
            ) : (
              <div className="flex gap-2">
                <button onClick={save} className="text-xs tracking-[0.18em] uppercase text-brand-primary" data-testid="editor-detail-save">Save</button>
                <button onClick={() => { setEditing(false); load(); }} className="text-xs tracking-[0.18em] uppercase text-brand-muted">Cancel</button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Row label="Name" value={editor.full_name} editing={editing} onChange={(v) => setDraft({ ...draft, full_name: v })} draft={draft.full_name} />
            <Row label="Phone" value={editor.phone_display || "—"} editing={editing} onChange={(v) => setDraft({ ...draft, phone: v })} draft={draft.phone} />
            <Row label="Email" value={editor.email || "—"} editing={editing} onChange={(v) => setDraft({ ...draft, email: v })} draft={draft.email} />
            <Row label="City" value={editor.city || "—"} editing={editing} onChange={(v) => setDraft({ ...draft, city: v })} draft={draft.city} />
            <div>
              <div className="kicker mb-1">SPECIALTY</div>
              {editing ? (
                <div className="flex gap-3" data-testid="editor-detail-specialty">
                  {["photo", "video", "both"].map((s) => (
                    <label key={s} className="flex items-center gap-1 cursor-pointer text-sm">
                      <input type="radio" checked={draft.specialty === s} onChange={() => setDraft({ ...draft, specialty: s })} />
                      {s}
                    </label>
                  ))}
                </div>
              ) : (
                <div className="text-sm" style={{ color: "var(--brand-ink)" }}>{editor.specialty}</div>
              )}
            </div>
            <div>
              <div className="kicker mb-1">ACTIVE</div>
              {editing ? (
                <input type="checkbox" checked={!!draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} data-testid="editor-detail-active" />
              ) : (
                <div className="text-sm" style={{ color: "var(--brand-ink)" }}>{editor.active ? "Yes" : "No"}</div>
              )}
            </div>
          </div>
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" data-testid="editor-stat-strip">
          <Stat label="Projects worked" value={s.projects_worked || 0} />
          <Stat label="Deliverables" value={`${s.deliverables_total || 0} total · ${s.deliverables_delivered || 0} delivered`} />
          <Stat label="Total paid" value={fmtINR(s.total_paid || 0)} />
          <Stat label="Last paid" value={fmtDate(s.last_paid) || "—"} />
        </div>

        {/* Project ledger */}
        <div className="bg-white border border-brand-border p-6" data-testid="editor-project-ledger">
          <h2 className="font-display text-lg mb-4" style={{ color: "var(--brand-ink)" }}>Project ledger</h2>
          {projects.length === 0 ? (
            <div className="text-sm italic text-brand-muted">No projects yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-brand-muted text-[11px] tracking-[0.16em] uppercase border-b border-brand-border">
                  <th className="py-2">Project</th><th className="py-2">Wedding</th><th className="py-2">Services</th><th className="py-2 text-right">Paid</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => (
                  <tr key={p.project_id} className="border-b border-brand-border" data-testid={`ledger-row-${p.project_id}`}>
                    <td className="py-2">
                      <Link to={`/projects/${p.project_id}`} className="hover:underline" style={{ color: "var(--brand-ink)" }}>{p.client_name || p.project_id}</Link>
                    </td>
                    <td className="py-2 text-brand-muted">{fmtDate(p.wedding_date) || "—"}</td>
                    <td className="py-2 text-brand-muted">{p.services.join(", ") || "—"}</td>
                    <td className="py-2 text-right">{fmtINR(p.paid)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {resetOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(14,48,48,0.45)" }} onClick={() => setResetOpen(false)} data-testid="editor-detail-reset-modal">
          <div className="bg-white p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="kicker mb-3">RESET PASSWORD · {editor.full_name}</div>
            <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="New password (min 8)" className="w-full text-sm border border-brand-border px-2 py-1.5" data-testid="editor-detail-reset-input" />
            <div className="flex gap-2 mt-4">
              <button onClick={resetPwd} className="px-4 py-2 text-xs tracking-[0.18em] uppercase text-white" style={{ background: "var(--brand-primary)" }} data-testid="editor-detail-reset-save">Reset</button>
              <button onClick={() => setResetOpen(false)} className="px-4 py-2 text-xs tracking-[0.18em] uppercase text-brand-muted">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Row({ label, value, editing, onChange, draft }) {
  return (
    <div>
      <div className="kicker mb-1">{label.toUpperCase()}</div>
      {editing ? (
        <input value={draft || ""} onChange={(e) => onChange(e.target.value)} className="w-full text-sm border border-brand-border px-2 py-1.5" />
      ) : (
        <div className="text-sm" style={{ color: "var(--brand-ink)" }}>{value}</div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-brand-border p-4">
      <div className="kicker mb-1">{label.toUpperCase()}</div>
      <div className="text-base" style={{ color: "var(--brand-ink)" }}>{value}</div>
    </div>
  );
}
